/**
 * @lines98/client/lib/attract-beat — ПЕРЕИСПОЛЬЗУЕМЫЙ модуль ТРИГГЕРНОГО «беата»
 * primary-CTA (мягкий намёк / сильный зов через масштаб-пульс кнопки).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-final 2026-06-28): прежнее ПОСТОЯННОЕ «дыхание»
 *   кнопки (infinite breathe `.cta-attract`) owner ОТВЕРГ — раздражает и глаз
 *   привыкает (фон, перестаёт замечаться). Заменяем на ГИБРИД-привлечение:
 *     (1) `beatSoft` — ОДИН тихий пульс ПРИ ЗАХОДЕ на экран (через короткую задержку,
 *         чтобы заметили), затем тишина;
 *     (2) `beatStrong` — СИЛЬНЫЙ зов (3 удара), когда игрок ТЫКАЕТ В ДОСКУ-превью
 *         (превью не активно → тап = промах) — кнопка «бьётся», уводя взгляд к себе;
 *         навешивается через `attachBoardNudge` (главный контекстный механизм).
 *   ОДИН модуль на ОБА хоста («Начать» board pre-start + «Играть» daily idle) —
 *   модель «ствол → ветки», не дублировать беат-логику по экранам (owner-directive
 *   модульности, code-structure §1/§3).
 *
 * SCOPE (что владеет): навеска finite-класса беата, RESTART уже идущего беата
 *   (force-reflow → можно пере-триггерить на повторный промах-тап), уборка класса по
 *   `animationend`; слушатель промах-тапа по доске (`attachBoardNudge`). Стили
 *   co-located — ./attract-beat.css (амплитуда/тайминг там, безразмерный scale).
 * NOT IN SCOPE: press-feedback depress (styles/press-feedback.css) — комплементарный
 *   примитив подтверждения НАЖАТИЯ. attract зовёт, press подтверждает.
 *   КОГДА планировать soft-беат (задержка mount) — решает экран
 *   (`scheduleSoftBeat` лишь владеет таймером+отменой, чтобы не дублировать setTimeout).
 *
 * ПЕРФ/ДОСТУПНОСТЬ: беат — composite-only (transform:scale), FINITE-анимация; класс
 *   снимается по `animationend` (will-change не «висит», беат пере-триггерим).
 *   `prefers-reduced-motion: reduce` → no-op (класс НЕ навешивается — иначе при
 *   `animation:none` событие animationend не придёт и класс «зависнет»). Слушатель
 *   промах-тапа ПАССИВНЫЙ (не preventDefault/stopPropagation) → не мешает реальным
 *   кликам по клеткам доски после старта (cell-handlers получают событие как раньше).
 *
 * Cross-references: ./attract-beat.css (классы/keyframes); styles/press-feedback.css
 *   (press-пара); screens/board-screen + screens/daily-board-screen
 *   (хосты); ui-performance SKILL §1.3 (composite-only, FINITE); code-structure §3
 *   (attachX → handle с detach()).
 */

import './attract-beat.css';

const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const SOFT_CLASS = 'cta-beat-soft';
const STRONG_CLASS = 'cta-beat-strong';
// Длительности должны совпадать с ./attract-beat.css (safety-таймер уборки класса,
// если animationend пропущен — напр. узел стал невидим). +80мс запас.
const SOFT_MS = 500;
const STRONG_MS = 780;
/** Задержка soft-намёка при заходе на экран (НЕ на 0-м кадре — чтобы заметили + не
 *  ловиться измерениями autofit/layout, и тест-замеры покоя не видят transient-scale). */
const ENTRANCE_HINT_DELAY_MS = 600;

export interface BoardNudgeHandle {
  /** Снять слушатель промах-тапа по доске (вызывать в teardown/unmount ветки). */
  detach(): void;
}

export interface SoftBeatHandle {
  /** Отменить отложенный soft-намёк, если экран сменился раньше срабатывания. */
  cancel(): void;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia(REDUCE_MOTION_QUERY).matches;
}

/**
 * Навесить беат данного класса, ПЕРЕЗАПУСТИВ анимацию если она уже идёт (force-reflow
 * trick): снять оба беат-класса → force reflow → навесить целевой. Снимается по
 * `animationend` → элемент готов к повторному беату (контекстный re-trigger). reduce →
 * no-op. `e.target !== el` гейт игнорирует bubbled animationend дочерних узлов,
 * чтобы чужая анимация не сняла наш беат-класс раньше времени.
 */
function beat(el: HTMLElement, className: string, durationMs: number): void {
  if (prefersReducedMotion()) return;

  // Restart: снять оба класса + force reflow → анимация перезапустится с 0% даже если
  // предыдущий беат ещё доигрывал (иначе повторный add класса не ре-триггерит CSS-анимацию).
  el.classList.remove(SOFT_CLASS, STRONG_CLASS);
  void el.offsetWidth; // force reflow
  el.classList.add(className);

  let done = false;
  const finish = (): void => {
    if (done) return;
    done = true;
    el.removeEventListener('animationend', onEnd);
    el.classList.remove(className);
  };
  const onEnd = (e: AnimationEvent): void => {
    if (e.target !== el) return; // не наша анимация (bubbled от ребёнка) — игнор
    finish();
  };
  el.addEventListener('animationend', onEnd);
  // Safety: если animationend не придёт (узел потерял видимость) — снять класс по таймеру.
  window.setTimeout(finish, durationMs + 80);
}

/** Мягкий намёк — один пульс (scale ~1.04). Зовёт взгляд к кнопке при заходе. */
export function beatSoft(el: HTMLElement): void {
  beat(el, SOFT_CLASS, SOFT_MS);
}

/** Сильный зов — три удара (scale ~1.06). Контекстный ответ на промах-тап по доске. */
export function beatStrong(el: HTMLElement): void {
  beat(el, STRONG_CLASS, STRONG_MS);
}

/**
 * Слушать промах-тап по доске-превью: на `pointerdown` по `boardEl` бьём `btnEl`
 * сильным беатом (зовём игрока к настоящей CTA). Слушатель ПАССИВНЫЙ — не отменяет
 * событие, поэтому реальные клики по клеткам после старта работают как раньше (это
 * лишь дополнительный наблюдатель). reduce → beatStrong сам no-op'ит. Возвращает
 * handle с `detach()` для чистого размонтирования экраном.
 */
export function attachBoardNudge(boardEl: HTMLElement, btnEl: HTMLElement): BoardNudgeHandle {
  const onPointerDown = (): void => {
    beatStrong(btnEl);
  };
  boardEl.addEventListener('pointerdown', onPointerDown);
  return {
    detach(): void {
      boardEl.removeEventListener('pointerdown', onPointerDown);
    },
  };
}

/**
 * Запланировать ОДИН soft-намёк через задержку входа (дефолт 600мс): «оживить» кнопку
 * вскоре после mount, чтобы игрок её заметил. Владеет таймером + `cancel()` (экран
 * сбрасывает при teardown/смене состояния раньше срабатывания — без беата на пустоту).
 * Вынесено в модуль, чтобы не дублировать setTimeout-уборку в двух экранах.
 */
export function scheduleSoftBeat(el: HTMLElement, delayMs: number = ENTRANCE_HINT_DELAY_MS): SoftBeatHandle {
  const timer = window.setTimeout(() => {
    beatSoft(el);
  }, delayMs);
  return {
    cancel(): void {
      window.clearTimeout(timer);
    },
  };
}
